import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, useOutletContext } from "react-router-dom";
import { Building2, User, Phone, Mail, ChevronDown, ChevronLeft, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import "react-datepicker/dist/react-datepicker.css";
import { DropdownOption } from "../../types/dropdown";
import { WorkerForm, Item, FormDataType } from "../../types/workpermit";
import ImagePreviewModal from "../../components/workPermit/ImagePreviewModal";
import SystemInformation from "./components/workPermit/SystemInformation";
import WorkPermitDetails from "./components/workPermit/WorkPermitDetails";
import WorkerModal from "./components/workPermit/WorkerModal";
import MaterialModal from "./components/workPermit/MaterialModal";
import MaterialInPermitDetails from "./components/workPermit/MaterialInPermitDetails";
import MaterialOutPermitDetails from "./components/workPermit/MaterialOutPermitDetails";
import { resolveImageUrl } from "../../utils/imageHelper";
import { GetWorkPermitType, GetTenantMall, SaveWorkPermit, GetActivityMaster, GetTenantEmployee, DropdownClassData } from "../../services/workPermitFormService";
import { GetWorkPermit } from "../../services/workPermitService";
import { GetRole } from "../../services/user";

const PERMIT_TYPES = [
  { value: 1, label: "Work Permit" },
  { value: 2, label: "Material In Permit" },
  { value: 3, label: "Material Out Permit" },
];

const PREPARED_BY_OPTIONS = [
  { value: "Tenant/Supplier", label: "Tenant/Supplier" },
  { value: "Contractor/Vendor", label: "Vendor" },
];

const getDbPermitTypeId = (val: number): number => {
  if (val === 1) return 30;
  if (val === 2) return 10;
  if (val === 3) return 20;
  return 0;
};

type LayoutContextType = {
  setHeaderTitle: (title: string | undefined) => void;
};

const getMallInitials = (mallName: string): string => {
  if (!mallName) return "";
  const words = mallName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 3).toUpperCase();
  }
  return words
    .map(word => word[0])
    .join("")
    .toUpperCase();
};

const generateAppNumber = (mallName: string) => {
  const initials = getMallInitials(mallName);
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  return `${initials}/WP/${day}${month}${year}`;
};

export default function WorkPermitForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isVendorContext = location.pathname.includes("vendor-management");
  const backPath = isVendorContext ? "/vendor-management/work-permit" : "/workPermit";
  const { MallId = 0, TenantOptions = [], viewMode = false, initialPermitTypeId, targetPermitType } = (location.state as { MallId?: number; TenantOptions: DropdownOption[]; viewMode?: boolean; initialPermitTypeId?: number; targetPermitType?: number }) || {};
  const isViewMode = viewMode || false;
  const isEdit = Boolean(id);
  const { setHeaderTitle } = useOutletContext<LayoutContextType>();
  const [mallId, setMallId] = useState<number>(MallId);
  const [tenantId, setTenantId] = useState<number>(0);
  const [workPermitHeaderId, setWorkPermitHeaderId] = useState<number>(0);
  const [permitHeaderStatus, setPermitHeaderStatus] = useState<number>(10);
  const [headerStatus, setHeaderStatus] = useState<number>(1);
  const [selectedPermitTypes, setSelectedPermitTypes] = useState<number[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchQuery, setSearchQuery] = useState("");
  const [appNumber, setAppNumber] = useState(() => generateAppNumber(""));
  const [permitNumber, setPermitNumber] = useState(appNumber);
  const [workCategory, setWorkCategory] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [status, setStatus] = useState("Draft");
  const [headerStartOn, setHeaderStartOn] = useState<string>("");
  const [loadedWorkPermitDatas, setLoadedWorkPermitDatas] = useState<any[]>([]);
  const [workCategoryOptions, setWorkCategoryOptions] = useState<{ value: string; label: string; id: number }[]>([]);
  const [materialInOptions, setMaterialInOptions] = useState<{ value: string; label: string; id: number }[]>([]);
  const [materialOutOptions, setMaterialOutOptions] = useState<{ value: string; label: string; id: number }[]>([]);
  const [materialInReason, setMaterialInReason] = useState("");
  const [materialOutReason, setMaterialOutReason] = useState("");
  const [, setMaterialAccessCard] = useState("");
  const [, setMaterialKeyParkingLot] = useState("");
  const [loadingArea, setLoadingArea] = useState("");

  useEffect(() => {
    const key = `workpermit_loading_area_${id || workPermitHeaderId || "draft"}`;
    const savedArea = localStorage.getItem(key);
    if (savedArea) {
      setLoadingArea(savedArea);
    }
  }, [id, workPermitHeaderId]);

  useEffect(() => {
    const key = `workpermit_loading_area_${id || workPermitHeaderId || "draft"}`;
    if (loadingArea) {
      localStorage.setItem(key, loadingArea);
    }
  }, [loadingArea, id, workPermitHeaderId]);
  const [workPermitWorkerIndices, setWorkPermitWorkerIndices] = useState<number[]>([]);
  const [materialInWorkerIndices, setMaterialInWorkerIndices] = useState<number[]>([]);
  const [materialOutWorkerIndices, setMaterialOutWorkerIndices] = useState<number[]>([]);
  const [materialSearchQuery, setMaterialSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  // Separate schedules for each permit type
  const [wpDateRange, setWpDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [wpStartDate, wpEndDate] = wpDateRange;
  const [wpStartTime, setWpStartTime] = useState<Date | null>(null);
  const [wpEndTime, setWpEndTime] = useState<Date | null>(null);

  const [matInDateRange, setMatInDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [matInStartDate, matInEndDate] = matInDateRange;
  const [matInStartTime, setMatInStartTime] = useState<Date | null>(null);
  const [matInEndTime, setMatInEndTime] = useState<Date | null>(null);

  const [matOutDateRange, setMatOutDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [matOutStartDate, matOutEndDate] = matOutDateRange;
  const [matOutStartTime, setMatOutStartTime] = useState<Date | null>(null);
  const [matOutEndTime, setMatOutEndTime] = useState<Date | null>(null);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [permitTypes, setPermitTypes] = useState<{ value: number; label: string; referenceid: number }[]>(
    PERMIT_TYPES.map((p) => ({ ...p, referenceid: 0 }))
  );
  const [workerModalMode, setWorkerModalMode] = useState<"add" | "edit">("add");
  const [workerFormState, setWorkerFormState] = useState<WorkerForm>({
    PersonnelName: "",
    IdentityNumber: "",
    DataImage: "",
    ImageLabel: "Foto KTP",
    IsAccessCard: 0,
    IsParkingLotKey: 0,
  });
  const [editingWorkerIndex, setEditingWorkerIndex] = useState<number | null>(null);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [materialModalMode, setMaterialModalMode] = useState<"add" | "edit">("add");
  const [materialFormState, setMaterialFormState] = useState<Item>({
    Description: "",
    ItemQuantity: 0,
    ItemUnit: "",
  });
  const [editingMaterial, setEditingMaterial] = useState<Item | null>(null);
  const [modalPermitType, setModalPermitType] = useState<number>(10);
  const [showPreviewImageModal, setShowPreviewImageModal] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedWorkerQR, setSelectedWorkerQR] = useState<{ name: string; qr: string } | null>(null);
  const [employeeDropdownOptions, setEmployeeDropdownOptions] = useState<DropdownClassData[]>([]);
  const [formData, setFormData] = useState<FormDataType & {
    activityName: string;
    activityDescription: string;
  }>({
    PermitTypeName: "Work Permit",
    nomorSuratIzin: appNumber,
    diajukanOleh: "Tenant/Supplier",
    TenantName: TenantOptions?.[0]?.label?.split("|")?.[0] ?? "",
    lot: TenantOptions?.[0]?.label?.split("|")?.[1] ?? "",
    PICName: "",
    PICPhone: "",
    PICAddress: "",
    StartOn: "",
    StartTime: "",
    alasanMemasukkanBarang: [],
    lokasiLoading: "",
    itemDatas: [],
    personnelDatas: [],
    activityName: "",
    activityDescription: "",
  });

  useEffect(() => {
    let isMounted = true;
    GetRole()
      .then((res) => {
        let refId = 0;
        if (res) {
          if (Array.isArray(res.dropdownClassDatas) && res.dropdownClassDatas.length > 0) {
            refId = res.dropdownClassDatas[0].DataId || 0;
          } else if (Array.isArray(res.userRoleDatas) && res.userRoleDatas.length > 0) {
            refId = res.userRoleDatas[0].roleData?.RoleId || res.userRoleDatas[0].UserRoleId || 0;
          } else if (res.roleData?.RoleId) {
            refId = res.roleData.RoleId;
          } else if (res.RoleId) {
            refId = res.RoleId;
          } else if (typeof res === "number") {
            refId = res;
          }
        }
        return GetTenantEmployee(refId);
      })
      .then((response) => {
        if (isMounted && response?.status === 200 && response.data?.dropdownClassDatas) {
          setEmployeeDropdownOptions(response.data.dropdownClassDatas);
        }
      })
      .catch((err) => {
        console.error("Failed to load tenant employee dropdown from API", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectEmployee = (
    emp: DropdownClassData,
    targetSection: "wp" | "matIn" | "matOut"
  ) => {
    let existingIndex = formData.personnelDatas.findIndex(
      (p) => p.PersonnelName.trim().toLowerCase() === emp.DataLabel.trim().toLowerCase()
    );

    if (existingIndex === -1) {
      const newWorker: WorkerForm = {
        PersonnelName: emp.DataLabel,
        IdentityNumber: emp.DataCode || emp.Description || "",
        DataImage: "",
        ImageLabel: "Foto KTP",
        IsAccessCard: 0,
        IsParkingLotKey: 0,
      };
      setFormData((prev) => {
        const nextPersonnel = [...prev.personnelDatas, newWorker];
        const newIdx = nextPersonnel.length - 1;
        if (targetSection === "wp") {
          setWorkPermitWorkerIndices((prevIndices) =>
            prevIndices.includes(newIdx) ? prevIndices : [...prevIndices, newIdx]
          );
        } else if (targetSection === "matIn") {
          setMaterialInWorkerIndices((prevIndices) =>
            prevIndices.includes(newIdx) ? prevIndices : [...prevIndices, newIdx]
          );
        } else if (targetSection === "matOut") {
          setMaterialOutWorkerIndices((prevIndices) =>
            prevIndices.includes(newIdx) ? prevIndices : [...prevIndices, newIdx]
          );
        }
        return { ...prev, personnelDatas: nextPersonnel };
      });
    } else {
      if (targetSection === "wp") {
        setWorkPermitWorkerIndices((prevIndices) =>
          prevIndices.includes(existingIndex) ? prevIndices : [...prevIndices, existingIndex]
        );
      } else if (targetSection === "matIn") {
        setMaterialInWorkerIndices((prevIndices) =>
          prevIndices.includes(existingIndex) ? prevIndices : [...prevIndices, existingIndex]
        );
      } else if (targetSection === "matOut") {
        setMaterialOutWorkerIndices((prevIndices) =>
          prevIndices.includes(existingIndex) ? prevIndices : [...prevIndices, existingIndex]
        );
      }
    }
  };

  useEffect(() => {
    setHeaderTitle("Permit Application Form");

    GetWorkPermitType(0)
      .then((response) => {
        if (response.status === 200 && response.data?.dropdownClassDatas) {
          const list = response.data.dropdownClassDatas;
          const mapped = list.map((item: any) => {
            let internalVal = 1;
            const label = item.DataLabel.toLowerCase();
            if (item.DataId === 10 || label.includes("masuk") || label.includes("material in") || label.includes("entry")) {
              internalVal = 2;
            } else if (item.DataId === 20 || label.includes("keluar") || label.includes("material out") || label.includes("exit")) {
              internalVal = 3;
            } else if (item.DataId === 30 || label.includes("kerja") || label.includes("work")) {
              internalVal = 1;
            }
            return {
              value: internalVal,
              label: item.DataLabel,
              referenceid: item.DataId,
            };
          });
          mapped.sort((a: any, b: any) => a.value - b.value);
          setPermitTypes(mapped);

          const wpType = mapped.find((p: any) => p.referenceid === 30 || p.value === 1);
          if (wpType) {
            GetActivityMaster(wpType.referenceid).then((res) => {
              if (res.status === 200 && res.data?.dropdownClassDatas) {
                const opts = res.data.dropdownClassDatas.map((item: any) => ({
                  value: item.DataLabel,
                  label: item.DataLabel,
                  id: item.DataId,
                }));
                setWorkCategoryOptions(opts);
                if (opts.length === 1) {
                  setWorkCategory(opts[0].value);
                }
              }
            });
          }

          const matInType = mapped.find((p: any) => p.referenceid === 10 || p.value === 2);
          if (matInType) {
            GetActivityMaster(matInType.referenceid).then((res) => {
              if (res.status === 200 && res.data?.dropdownClassDatas) {
                const opts = res.data.dropdownClassDatas.map((item: any) => ({
                  value: item.DataLabel,
                  label: item.DataLabel,
                  id: item.DataId,
                }));
                setMaterialInOptions(opts);
                if (opts.length === 1) {
                  setMaterialInReason(opts[0].value);
                }
              }
            });
          }

          const matOutType = mapped.find((p: any) => p.referenceid === 20 || p.value === 3);
          if (matOutType) {
            GetActivityMaster(matOutType.referenceid).then((res) => {
              if (res.status === 200 && res.data?.dropdownClassDatas) {
                const opts = res.data.dropdownClassDatas.map((item: any) => ({
                  value: item.DataLabel,
                  label: item.DataLabel,
                  id: item.DataId,
                }));
                setMaterialOutOptions(opts);
                if (opts.length === 1) {
                  setMaterialOutReason(opts[0].value);
                }
              }
            });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load permit types or activity masters from API", err);
      });

    return () => {
      setHeaderTitle(undefined);
    };
  }, [setHeaderTitle]);

  useEffect(() => {
    if (isEdit) return;
    const tid = parseInt(
      localStorage.getItem("tenant_id") ||
      sessionStorage.getItem("tenant_id") ||
      "0"
    );
    setTenantId(tid);

    if (tid > 0) {
      GetTenantMall(tid)
        .then((response) => {
          if (response.status === 200 && response.data?.tenantMallDatas?.length > 0) {
            const data = response.data.tenantMallDatas[0];
            setMallId(data.MallId || 0);
            const newAppNo = generateAppNumber(data.MallName);
            setAppNumber(newAppNo);
            setPermitNumber(newAppNo);
            setFormData((prev) => ({
              ...prev,
              nomorSuratIzin: newAppNo,
              TenantName: data.BrandName || prev.TenantName,
              lot: data.Unit || prev.lot,
              PICName: data.PICName || prev.PICName,
              PICPhone: data.PICPhone || prev.PICPhone,
              PICAddress: data.PICEmail || prev.PICAddress,
            }));
          }
        })
        .catch((err) => {
          console.error("Failed to load tenant mall details", err);
        });
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit && id) {
      const parsedId = parseInt(id);
      Swal.fire({
        title: "Loading...",
        text: "Memuat data permit...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      const mid = location.state?.MallId || parseInt(
        localStorage.getItem("mall_id") ||
        sessionStorage.getItem("mall_id") ||
        "0"
      ) || 0;
      GetWorkPermit(parsedId, 0, 0, mid, 0, 0)
        .then((response) => {
          Swal.close();
          if (response.workPermitHeaderDatas && response.workPermitHeaderDatas.length > 0) {
            const header = response.workPermitHeaderDatas[0];
            setMallId(header.MallId);
            setTenantId(header.TenantId);
            setWorkPermitHeaderId(header.WorkPermitHeaderId);
            setPermitHeaderStatus(header.PermitHeaderStatus || 1);
            setHeaderStatus(header.Status || 1);
            setHeaderStartOn(header.StartOn || "");

            const headerPermitNo =
              header.PermitNumberHeader ||
              header.PermitNumber ||
              (header as any).PermitHeaderNumber ||
              (header as any).PermitHeaderNo ||
              (header as any).PermitNo ||
              (header as any).WorkPermitNumber ||
              (header.workPermitDatas && header.workPermitDatas.length > 0 ? header.workPermitDatas[0].PermitNumber : "") ||
              "";
            setAppNumber(headerPermitNo);
            setPermitNumber(headerPermitNo);

            setLoadedWorkPermitDatas(header.workPermitDatas || []);

            const workPermit = header.workPermitDatas?.find((p: any) => p.PermitTypeId === 30);
            const materialInPermit = header.workPermitDatas?.find((p: any) => p.PermitTypeId === 10);
            const materialOutPermit = header.workPermitDatas?.find((p: any) => p.PermitTypeId === 20);

            setWorkCategory(workPermit?.ActivityMasterName || "");
            setMaterialInReason(materialInPermit?.ActivityMasterName || "");
            setMaterialOutReason(materialOutPermit?.ActivityMasterName || "");

            const permitDesc = workPermit?.Description || header.Description || "";
            setWorkDescription(permitDesc);
            // Status Permohonan diambil dari field PermitHeaderStatus (header)
            let headerStatusText = header.PermitHeaderStatusDesc || (header as any).PermitHeaderStatusText || "";
            if (!headerStatusText) {
              const hs = header.PermitHeaderStatus ?? (header as any).PermitStatus;
              if (hs === 10) headerStatusText = "Pending Approval";
              else if (hs === 20) headerStatusText = "Approved";
              else if (hs === 30) headerStatusText = "Reject";
              else if (hs === 40) headerStatusText = "Waiting";
              else headerStatusText = "Draft";
            }
            setStatus(headerStatusText);

            // Set date range
            if (header.StartOn && header.FinishOn) {
              setDateRange([new Date(header.StartOn), new Date(header.FinishOn)]);
            } else if (header.StartOn) {
              setDateRange([new Date(header.StartOn), new Date(header.StartOn)]);
            }

            // Set times
            if (header.StartTime) {
              const [h, m, s] = header.StartTime.split(":").map(Number);
              const d = new Date();
              d.setHours(h, m, s || 0);
              setStartTime(d);
            }
            if (header.FinishTime) {
              const [h, m, s] = header.FinishTime.split(":").map(Number);
              const d = new Date();
              d.setHours(h, m, s || 0);
              setEndTime(d);
            }

            // Populate separate schedules for each permit type if they exist in DB
            if (workPermit) {
              if (workPermit.StartOn && workPermit.FinishOn) {
                setWpDateRange([new Date(workPermit.StartOn), new Date(workPermit.FinishOn)]);
              } else if (workPermit.StartOn) {
                setWpDateRange([new Date(workPermit.StartOn), new Date(workPermit.StartOn)]);
              }
              if (workPermit.StartTime) {
                const [h, m, s] = workPermit.StartTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setWpStartTime(d);
              }
              if (workPermit.FinishTime) {
                const [h, m, s] = workPermit.FinishTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setWpEndTime(d);
              }
            } else {
              setWpDateRange([null, null]);
              setWpStartTime(null);
              setWpEndTime(null);
            }

            if (materialInPermit) {
              if (materialInPermit.StartOn && materialInPermit.FinishOn) {
                setMatInDateRange([new Date(materialInPermit.StartOn), new Date(materialInPermit.FinishOn)]);
              } else if (materialInPermit.StartOn) {
                setMatInDateRange([new Date(materialInPermit.StartOn), new Date(materialInPermit.StartOn)]);
              }
              if (materialInPermit.StartTime) {
                const [h, m, s] = materialInPermit.StartTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setMatInStartTime(d);
              }
              if (materialInPermit.FinishTime) {
                const [h, m, s] = materialInPermit.FinishTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setMatInEndTime(d);
              }
            } else {
              setMatInDateRange([null, null]);
              setMatInStartTime(null);
              setMatInEndTime(null);
            }

            if (materialOutPermit) {
              if (materialOutPermit.StartOn && materialOutPermit.FinishOn) {
                setMatOutDateRange([new Date(materialOutPermit.StartOn), new Date(materialOutPermit.FinishOn)]);
              } else if (materialOutPermit.StartOn) {
                setMatOutDateRange([new Date(materialOutPermit.StartOn), new Date(materialOutPermit.StartOn)]);
              }
              if (materialOutPermit.StartTime) {
                const [h, m, s] = materialOutPermit.StartTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setMatOutStartTime(d);
              }
              if (materialOutPermit.FinishTime) {
                const [h, m, s] = materialOutPermit.FinishTime.split(":").map(Number);
                const d = new Date();
                d.setHours(h, m, s || 0);
                setMatOutEndTime(d);
              }
            } else {
              setMatOutDateRange([null, null]);
              setMatOutStartTime(null);
              setMatOutEndTime(null);
            }

            const selectedTypes: number[] = [];
            if (header.PermitTypeIds) {
              header.PermitTypeIds.forEach((typeId) => {
                if (typeId === 30) selectedTypes.push(1);
                else if (typeId === 10) selectedTypes.push(2);
                else if (typeId === 20) selectedTypes.push(3);
              });
            }
            setSelectedPermitTypes(selectedTypes);

            const personnel = (header.personnelDatas || []).map((p: any) => ({
              WorkPermitPersonnelId: p.WorkPermitPersonnelId || 0,
              WorkPermitHeaderId: p.WorkPermitHeaderId || 0,
              PersonnelName: p.PersonnelName || "",
              IdentityNumber: p.IdentityNumber || "",
              DataImage: resolveImageUrl(p.FileImage) || "",
              FileImage: p.FileImage || null,
              ImageLabel: p.ImageLabel || "Foto KTP",
              IsAccessCard: p.IsAccessCard || 0,
              IsParkingLotKey: p.IsParkingLotKey || 0,
              PersonnelQRCode: p.PersonnelQRCode || "",
              CheckInStatus: p.CheckInStatus || 0,
              Status: p.Status || 1,
            }));

            const items = (header.itemDatas || []).map((i: any) => ({
              Description: i.Description || "",
              ItemQuantity: i.ItemQuantity || 0,
              ItemUnit: i.ItemUnit || "",
              WorkPermitItemId: i.WorkPermitItemId || 0,
              PermitTypeId: i.PermitTypeId,
              Status: i.Status || 1,
            }));

            setFormData((prev) => ({
              ...prev,
              nomorSuratIzin: headerPermitNo,
              diajukanOleh: header.VendorId ? "Contractor/Vendor" : "Tenant/Supplier",
              TenantName: header.TenantName || prev.TenantName,
              lot: header.LotNo || prev.lot,
              PICName: header.PICName || "",
              PICPhone: header.PICPhone || "",
              PICAddress: header.PICAddress || "",
              StartOn: header.StartOn ? header.StartOn.split("T")[0] : prev.StartOn,
              StartTime: header.StartTime || prev.StartTime,
              activityDescription: permitDesc,
              personnelDatas: personnel,
              itemDatas: items,
            }));

            const indices = personnel.map((_, idx) => idx);
            setWorkPermitWorkerIndices(workPermit ? indices : []);
            setMaterialInWorkerIndices(materialInPermit ? indices : []);
            setMaterialOutWorkerIndices(materialOutPermit ? indices : []);
          }
        })
        .catch((err) => {
          Swal.close();
          console.error("Failed to load work permit data for edit:", err);
          Swal.fire({
            title: "Error",
            text: "Gagal memuat data permit untuk pengubahan.",
            icon: "error",
            confirmButtonColor: "#2563eb"
          });
        });
    }
  }, [isEdit, id]);

  useEffect(() => {
    const hasAccessCardIn = materialInWorkerIndices.some(
      (idx) => formData.personnelDatas[idx]?.IsAccessCard === 1
    );
    const hasAccessCardOut = materialOutWorkerIndices.some(
      (idx) => formData.personnelDatas[idx]?.IsAccessCard === 1
    );
    setMaterialAccessCard((hasAccessCardIn || hasAccessCardOut) ? "Yes" : "No");

    const hasParkingKeyIn = materialInWorkerIndices.some(
      (idx) => formData.personnelDatas[idx]?.IsParkingLotKey === 1
    );
    const hasParkingKeyOut = materialOutWorkerIndices.some(
      (idx) => formData.personnelDatas[idx]?.IsParkingLotKey === 1
    );
    setMaterialKeyParkingLot((hasParkingKeyIn || hasParkingKeyOut) ? "Yes" : "No");
  }, [materialInWorkerIndices, materialOutWorkerIndices, formData.personnelDatas]);

  const sectionRefs = {
    1: useRef<HTMLDivElement>(null),
    2: useRef<HTMLDivElement>(null),
    3: useRef<HTMLDivElement>(null),
  } as Record<number, React.RefObject<HTMLDivElement>>;

  const scrollToSection = (value: number) => {
    setTimeout(() => {
      sectionRefs[value]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  useEffect(() => {
    const permitTypeIdVal = targetPermitType
      ? (targetPermitType === 1 ? 30 : targetPermitType === 2 ? 10 : 20)
      : initialPermitTypeId;

    if ((permitTypeIdVal || targetPermitType) && (loadedWorkPermitDatas.length > 0 || !isEdit)) {
      let targetSection = 0;
      if (targetPermitType) {
        targetSection = targetPermitType;
      } else if (Number(permitTypeIdVal) === 30) {
        targetSection = 1;
      } else if (Number(permitTypeIdVal) === 10) {
        targetSection = 2;
      } else if (Number(permitTypeIdVal) === 20) {
        targetSection = 3;
      }

      if (targetSection > 0) {
        scrollToSection(targetSection);
      }
    }
  }, [initialPermitTypeId, targetPermitType, loadedWorkPermitDatas, isEdit]);

  const hasAnyApprovedByDept = loadedWorkPermitDatas.some((wp: any) =>
    wp.PermitStatus === 20 ||
    wp.approvalDatas?.some(
      (a: any) =>
        a.WorkPermitApprovalStatusId === 20 ||
        a.WorkPermitApprovalStatusName?.toLowerCase() === "approved" ||
        a.status?.toLowerCase() === "approved"
    )
  );

  const isPermitTypeDisabled = (value: number) => {
    let dbPermitTypeId = 30;
    if (value === 1) dbPermitTypeId = 30;
    else if (value === 2) dbPermitTypeId = 10;
    else if (value === 3) dbPermitTypeId = 20;

    const isAlreadySavedInDB = isEdit && loadedWorkPermitDatas.some(
      (ep) => Number(ep.PermitTypeId) === dbPermitTypeId
    );

    if (isViewMode || hasAnyApprovedByDept || (isAlreadySavedInDB && selectedPermitTypes.includes(value))) {
      return true;
    }

    if (selectedPermitTypes.includes(value)) {
      return false;
    }

    if (selectedPermitTypes.length >= 2) {
      return true;
    }

    if (value === 1 && selectedPermitTypes.includes(2) && selectedPermitTypes.includes(3)) {
      return true;
    }

    if (selectedPermitTypes.includes(1)) {
      if (value === 2 && selectedPermitTypes.includes(3)) return true;
      if (value === 3 && selectedPermitTypes.includes(2)) return true;
    }

    return false;
  };

  const togglePermitType = (value: number) => {
    if (isViewMode || hasAnyApprovedByDept) {
      return;
    }

    let dbPermitTypeId = 30;
    if (value === 1) dbPermitTypeId = 30;
    else if (value === 2) dbPermitTypeId = 10;
    else if (value === 3) dbPermitTypeId = 20;

    const isAlreadySavedInDB = loadedWorkPermitDatas.some(
      (ep) => Number(ep.PermitTypeId) === dbPermitTypeId
    );

    if (isEdit && isAlreadySavedInDB && selectedPermitTypes.includes(value)) {
      return;
    }

    if (selectedPermitTypes.includes(value)) {
      setSelectedPermitTypes((prev) => prev.filter((v) => v !== value));
    } else {
      if (isPermitTypeDisabled(value)) {
        if (selectedPermitTypes.length >= 2) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "warning",
            title: "Maksimal memilih 2 Jenis Izin",
            showConfirmButton: false,
            timer: 2000,
          });
        }
        return;
      }
      setSelectedPermitTypes((prev) => [...prev, value]);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openAddWorkerModal = () => {
    setWorkerModalMode("add");
    setWorkerFormState({
      PersonnelName: "",
      IdentityNumber: "",
      DataImage: "",
      ImageLabel: "Foto KTP",
      IsAccessCard: 0,
      IsParkingLotKey: 0,
    });
    setEditingWorkerIndex(null);
    setShowWorkerModal(true);
  };

  const openEditWorkerModal = (worker: WorkerForm, index: number) => {
    setWorkerModalMode("edit");
    setWorkerFormState(worker);
    setEditingWorkerIndex(index);
    setShowWorkerModal(true);
  };

  const handleSaveWorker = (updatedWorker: WorkerForm) => {
    setFormData((prev) => {
      const list = [...prev.personnelDatas];
      if (workerModalMode === "add") {
        list.push(updatedWorker);
      } else if (editingWorkerIndex !== null) {
        list[editingWorkerIndex] = updatedWorker;
      }
      return { ...prev, personnelDatas: list };
    });
    setShowWorkerModal(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteWorker = (index: number) => {
    Swal.fire({
      title: "Hapus pekerja?",
      text: "Pekerja yang dihapus tidak bisa dikembalikan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setFormData((prev) => ({
        ...prev,
        personnelDatas: prev.personnelDatas.filter((_, i) => i !== index),
      }));

      Swal.fire({
        icon: "success",
        title: "Pekerja dihapus",
        timer: 1200,
        showConfirmButton: false,
      });
    });
  };

  const handleViewKtp = (imageSrc: string) => {
    if (!imageSrc) {
      Swal.fire({ title: "Error", text: "Foto KTP belum diupload", icon: "error" });
      return;
    }
    setImagePreview(resolveImageUrl(imageSrc));
    setShowPreviewImageModal(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDownloadKtp = (imageSrc: string, workerName: string) => {
    if (!imageSrc) {
      Swal.fire({ title: "Error", text: "Foto KTP belum diupload", icon: "error" });
      return;
    }
    const link = document.createElement("a");
    link.href = resolveImageUrl(imageSrc);
    const cleanName = workerName.trim().replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
    link.download = `ktp_${cleanName || "worker"}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openAddMaterialModal = (permitType: number) => {
    setMaterialModalMode("add");
    setModalPermitType(permitType);
    setMaterialFormState({
      Description: "",
      ItemQuantity: 0,
      ItemUnit: "",
    });
    setEditingMaterial(null);
    setShowMaterialModal(true);
  };

  const openEditMaterialModal = (material: Item) => {
    setMaterialModalMode("edit");
    if (material.PermitTypeId) {
      setModalPermitType(material.PermitTypeId);
    }
    setMaterialFormState(material);
    setEditingMaterial(material);
    setShowMaterialModal(true);
  };

  const handleSaveMaterial = (updatedMaterial: Item) => {
    setFormData((prev) => {
      let list = [...prev.itemDatas];
      const materialWithPermitType: Item = {
        ...updatedMaterial,
        PermitTypeId: modalPermitType,
        WorkPermitItemId: updatedMaterial.WorkPermitItemId || 0,
        Status: updatedMaterial.Status || 1,
      };

      if (materialModalMode === "add") {
        list.push(materialWithPermitType);
      } else if (editingMaterial !== null) {
        list = list.map((item) => item === editingMaterial ? materialWithPermitType : item);
      }
      return { ...prev, itemDatas: list };
    });
    setShowMaterialModal(false);
  };

  const handleDeleteMaterial = (itemToDelete: Item) => {
    Swal.fire({
      title: "Hapus material?",
      text: "Material yang dihapus tidak bisa dikembalikan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setFormData((prev) => ({
        ...prev,
        itemDatas: prev.itemDatas.filter((item) => item !== itemToDelete),
      }));

      Swal.fire({
        icon: "success",
        title: "Material dihapus",
        timer: 1200,
        showConfirmButton: false,
      });
    });
  };

  const handleWorkActivityChange = (val: string) => {
    setWorkCategory(val);
  };

  const handleMaterialInActivityChange = (val: string) => {
    setMaterialInReason(val);
  };

  const handleMaterialOutActivityChange = (val: string) => {
    setMaterialOutReason(val);
  };

  const handleSubmit = async () => {
    if (!formData.PICName.trim()) {
      Swal.fire({ title: "Validation error", text: "Nama PIC harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (!formData.PICPhone.trim()) {
      Swal.fire({ title: "Validation error", text: "Nomor PIC phone harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (!formData.PICAddress.trim()) {
      Swal.fire({ title: "Validation error", text: "Email PIC harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }

    const permitTypeActivities = selectedPermitTypes.map((val) => {
      const matched = permitTypes.find((p) => p.value === val);
      let activityName = "";
      let activityId = 0;

      if (val === 1) {
        activityName = workCategory;
        const opt = workCategoryOptions.find(
          (o) => o.value.trim().toLowerCase() === workCategory.trim().toLowerCase()
        );
        // FIX: gunakan mapping tetap, bukan matched.referenceid, supaya tidak
        // bergantung pada apakah GetWorkPermitType sudah selesai load atau belum.
        const refId = getDbPermitTypeId(val);
        const existingPermit = loadedWorkPermitDatas.find(
          (ep) => Number(ep.PermitTypeId) === Number(refId)
        );
        activityId = opt ? opt.id : (existingPermit ? existingPermit.ActivityMasterId : 0);
      } else if (val === 2) {
        activityName = materialInReason;
        const opt = materialInOptions.find(
          (o) => o.value.trim().toLowerCase() === materialInReason.trim().toLowerCase()
        );
        // FIX: gunakan mapping tetap, bukan matched.referenceid.
        const refId = getDbPermitTypeId(val);
        const existingPermit = loadedWorkPermitDatas.find(
          (ep) => Number(ep.PermitTypeId) === Number(refId)
        );
        activityId = opt ? opt.id : (existingPermit ? existingPermit.ActivityMasterId : 0);
      } else if (val === 3) {
        activityName = materialOutReason;
        const opt = materialOutOptions.find(
          (o) => o.value.trim().toLowerCase() === materialOutReason.trim().toLowerCase()
        );
        // FIX: gunakan mapping tetap, bukan matched.referenceid.
        const refId = getDbPermitTypeId(val);
        const existingPermit = loadedWorkPermitDatas.find(
          (ep) => Number(ep.PermitTypeId) === Number(refId)
        );
        activityId = opt ? opt.id : (existingPermit ? existingPermit.ActivityMasterId : 0);
      }

      return {
        // FIX: PermitTypeId dikirim ke backend memakai mapping tetap
        // (getDbPermitTypeId), bukan matched.referenceid, supaya selalu
        // konsisten dengan PermitTypeId yang tersimpan di DB (30/10/20)
        // dan backend bisa mencocokkan record yang sudah ada -> UPDATE,
        // bukan salah anggap permit type baru -> INSERT.
        PermitTypeId: getDbPermitTypeId(val),
        PermitTypeValue: val,
        PermitTypeLabel: matched?.label ?? "",
        ActivityMasterId: activityId,
        ActivityMasterName: activityName,
      };
    });

    if (permitTypeActivities.length === 0) {
      Swal.fire({ title: "Validation error", text: "Pilih minimal satu Permit Type", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }

    const missingActivity = permitTypeActivities.find((p) => p.ActivityMasterId === 0);
    if (missingActivity) {
      Swal.fire({
        title: "Validation error",
        text: `Activity harus dipilih untuk "${missingActivity.PermitTypeLabel || "permit type terpilih"}"`,
        icon: "error",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const activeWorkerIndices = new Set<number>();
    if (selectedPermitTypes.includes(1)) {
      workPermitWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
    }
    if (selectedPermitTypes.includes(2)) {
      materialInWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
    }
    if (selectedPermitTypes.includes(3)) {
      materialOutWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
    }

    const activeWorkers = formData.personnelDatas.filter((_, idx) => activeWorkerIndices.has(idx));

    if (activeWorkers.length === 0) {
      Swal.fire({
        title: "Validation error",
        text: "Harap tambahkan minimal 1 pekerja pada izin yang dipilih.",
        icon: "error",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const missingPhotoWorker = activeWorkers.find((w) => !w.DataImage || !w.DataImage.trim());
    if (missingPhotoWorker) {
      Swal.fire({
        title: "Validation error",
        text: `Pekerja "${missingPhotoWorker.PersonnelName}" belum upload foto KTP. Harap klik "Upload KTP & Kartu Akses/Kunci" untuk melengkapi foto KTP.`,
        icon: "error",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    Swal.fire({
      title: "Submitting...",
      text: "Please wait while we process your request.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const formatDateTime = (date: Date | null) => {
      if (!date) return new Date().toISOString();
      const tzoffset = date.getTimezoneOffset() * 60000;
      const localISOTime = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, -1);
      const offsetHours = Math.abs(Math.floor(date.getTimezoneOffset() / 60));
      const offsetMinutes = Math.abs(date.getTimezoneOffset() % 60);
      const sign = date.getTimezoneOffset() > 0 ? "-" : "+";
      const offsetStr = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
      return localISOTime + offsetStr;
    };

    const formatTime = (time: Date | null) => {
      if (!time) return "00:00:00.0000000";
      const hours = String(time.getHours()).padStart(2, "0");
      const minutes = String(time.getMinutes()).padStart(2, "0");
      const seconds = String(time.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}.0000000`;
    };

    const payload = {
      workPermitHeaderData: {
        WorkPermitHeaderId: isEdit ? workPermitHeaderId : 0,
        PermitNumber: permitNumber || null,
        MallId: mallId,
        TenantId: tenantId,
        TenantName: formData.TenantName,
        LotNo: formData.lot,
        VendorId: null,
        VendorName: null,
        PICName: formData.PICName,
        PICAddress: formData.PICAddress,
        PICPhone: formData.PICPhone,
        StartOn: formatDateTime(startDate),
        FinishOn: formatDateTime(endDate || startDate),
        StartTime: formatTime(startTime),
        FinishTime: formatTime(endTime),
        Description: workDescription || "",
        PermitHeaderStatus: isEdit ? permitHeaderStatus : 10,
        Status: isEdit ? headerStatus : 1,
        PermitTypeIds: permitTypeActivities.map((p) => p.PermitTypeId),
        PermitTypeActivities: permitTypeActivities.map((p) => ({
          PermitTypeId: p.PermitTypeId,
          ActivityMasterId: p.ActivityMasterId,
        })),
        ActivityMasterId: permitTypeActivities[0].ActivityMasterId,
        personnelDatas: (() => {
          const activeWorkerIndices = new Set<number>();
          if (selectedPermitTypes.includes(1)) {
            workPermitWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
          }
          if (selectedPermitTypes.includes(2)) {
            materialInWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
          }
          if (selectedPermitTypes.includes(3)) {
            materialOutWorkerIndices.forEach((idx) => activeWorkerIndices.add(idx));
          }

          return formData.personnelDatas
            .filter((_, idx) => activeWorkerIndices.has(idx))
            .map((worker) => {
              let base64Data = "";
              let filename = worker.FileImage || null;

              if (worker.DataImage && typeof worker.DataImage === "string") {
                if (worker.DataImage.startsWith("data:")) {
                  // New upload — strip the data:image/... header
                  const parts = worker.DataImage.split(",");
                  if (parts.length > 1) {
                    base64Data = parts[1].replace(/\s/g, "");
                  }
                  const cleanName = worker.PersonnelName.trim().replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
                  filename = filename || `ktp_${cleanName || "worker"}.jpg`;
                } else if (
                  worker.DataImage.startsWith("http://") ||
                  worker.DataImage.startsWith("https://") ||
                  worker.DataImage.startsWith("/")
                ) {
                  base64Data = "";
                  if (!filename) filename = worker.DataImage;
                } else {
                  base64Data = worker.DataImage.replace(/\s/g, "");
                }
              }

              return {
                WorkPermitPersonnelId: worker.WorkPermitPersonnelId || 0,
                WorkPermitHeaderId: isEdit ? workPermitHeaderId : 0,
                PersonnelName: worker.PersonnelName,
                IdentityNumber: worker.IdentityNumber,
                PersonnelQRCode: worker.PersonnelQRCode || null,
                DataImage: base64Data,
                IsAccessCard: worker.IsAccessCard,
                IsParkingLotKey: worker.IsParkingLotKey,
                FileImage: filename,
                ImageLabel: worker.ImageLabel || "Foto KTP",
                CheckInStatus: 0,
                Status: 1,
              };
            });
        })(),
        itemDatas: formData.itemDatas
          .filter((item) => {
            if (item.PermitTypeId === 10 && selectedPermitTypes.includes(2)) return true;
            if (item.PermitTypeId === 20 && selectedPermitTypes.includes(3)) return true;
            return false;
          })
          .map((item) => ({
            WorkPermitItemId: item.WorkPermitItemId || 0,
            PermitTypeId: item.PermitTypeId || 10,
            ItemQuantity: item.ItemQuantity,
            ItemUnit: item.ItemUnit,
            Description: item.Description,
            Status: item.Status || 1,
          })),
        workPermitDatas: permitTypeActivities.map((p) => {
          const existingPermit = loadedWorkPermitDatas.find(
            (ep) => Number(ep.PermitTypeId) === Number(p.PermitTypeId)
          );

          let sDate: Date | null = null;
          let eDate: Date | null = null;
          let sTime: Date | null = null;
          let eTime: Date | null = null;

          if (p.PermitTypeValue === 1) {
            sDate = wpStartDate || startDate;
            eDate = wpEndDate || endDate;
            sTime = wpStartTime || startTime;
            eTime = wpEndTime || endTime;
          } else if (p.PermitTypeValue === 2) {
            sDate = matInStartDate || startDate;
            eDate = matInEndDate || endDate;
            sTime = matInStartTime || startTime;
            eTime = matInEndTime || endTime;
          } else if (p.PermitTypeValue === 3) {
            sDate = matOutStartDate || startDate;
            eDate = matOutEndDate || endDate;
            sTime = matOutStartTime || startTime;
            eTime = matOutEndTime || endTime;
          }

          return {
            WorkPermitId: existingPermit?.WorkPermitId || 0,
            PermitNumber: existingPermit?.PermitNumber || permitNumber || "",
            PermitTypeId: p.PermitTypeId,
            PermitTypeName: p.PermitTypeLabel,
            WorkPermitHeaderId: isEdit ? workPermitHeaderId : 0,
            ActivityMasterId: p.ActivityMasterId,
            ActivityMasterName: p.ActivityMasterName,
            StartOn: formatDateTime(sDate),
            FinishOn: formatDateTime(eDate || sDate),
            StartTime: formatTime(sTime),
            FinishTime: formatTime(eTime),
            Description: workDescription || "",
            PermitStatus: existingPermit?.PermitStatus || 10,
            PermitStatusDesc: existingPermit?.PermitStatusDesc || "Pending Approval",
            Status: existingPermit?.Status || 1,
          };
        }),
      },
    };

    try {
      const response = await SaveWorkPermit(payload);
      if (response.status === 200 && (response.data?.Code === 0 || response.data?.Code === 200)) {
        Swal.fire({
          title: "Success!",
          text: `Permit application form has been ${isEdit ? "updated" : "submitted"} successfully.`,
          icon: "success",
          confirmButtonColor: "#2563eb",
        }).then(() => {
          navigate(backPath);
        });
      } else {
        let msg = response.data?.Message || "Failed to save work permit.";
        if (msg.includes("EntityValidationErrors") || msg.includes("Validation failed")) {
          msg = "Gagal menyimpan: Harap upload foto KTP dan pilih Kartu Akses / Kunci Parkir untuk setiap pekerja.";
        }
        Swal.fire({
          title: "Validation Error",
          text: msg,
          icon: "error",
          confirmButtonColor: "#2563eb",
        });
      }
    } catch (err: any) {
      console.error("Error saving work permit", err);
      let msg = err?.response?.data?.Message || err?.message || "An error occurred while saving work permit.";
      if (msg.includes("EntityValidationErrors") || msg.includes("Validation failed")) {
        msg = "Gagal menyimpan: Harap upload foto KTP dan pilih Kartu Akses / Kunci Parkir untuk setiap pekerja.";
      }
      Swal.fire({
        title: "Validation Error",
        text: msg,
        icon: "error",
        confirmButtonColor: "#2563eb",
      });
    }
  };

  const handleDiscardDraft = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will lose all unsaved changes",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, discard it!",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(backPath);
      }
    });
  };

  const getPermitStatusName = (permitTypeId: number) => {
    const permit = loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === permitTypeId);
    if (!permit) return "Draft";

    let resolvedStatus =
      permit.PermitStatusDesc ||
      permit.WorkPermitApprovalStatusName ||
      permit.CurrentApprovalStatusName ||
      "";

    if (!resolvedStatus) {
      const statusId = permit.PermitStatus ?? permit.WorkPermitStatusId;
      if (statusId === 10) resolvedStatus = "Pending Approval";
      else if (statusId === 20) resolvedStatus = "Approved";
      else if (statusId === 30) resolvedStatus = "Reject";
      else if (statusId === 40) resolvedStatus = "Waiting";
    }

    return resolvedStatus || "Pending";
  };

  const getLastApprovalInfo = (permitTypeId: number) => {
    const workPermit = loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === permitTypeId);
    if (!workPermit || !workPermit.approvalDatas || workPermit.approvalDatas.length === 0) {
      return {
        dept: workPermit?.CurrentApprovalDepartment || "-",
        date: "-",
      };
    }

    const approved = workPermit.approvalDatas
      .filter((a: any) => a.WorkPermitApprovalStatusId === 20)
      .sort((a: any, b: any) => new Date(b.ModifiedOn || b.CreatedOn || 0).getTime() - new Date(a.ModifiedOn || a.CreatedOn || 0).getTime())[0];

    const lastApprovedByDept = approved
      ? (approved.DepartmentName || approved.departmentName || approved.department || workPermit.CurrentApprovalDepartment || "-")
      : (workPermit.CurrentApprovalDepartment || "-");
    const lastApprovedOn = approved ? (approved.ModifiedOn || approved.ApprovalDate || approved.CreatedOn) : null;

    let formattedDate = "-";
    if (lastApprovedOn) {
      try {
        const d = new Date(lastApprovedOn);
        formattedDate = isNaN(d.getTime())
          ? lastApprovedOn
          : d.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
      } catch {
        formattedDate = lastApprovedOn;
      }
    }

    return {
      dept: lastApprovedByDept,
      date: formattedDate,
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredWorkers = formData.personnelDatas.filter((worker) =>
    worker.PersonnelName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    worker.IdentityNumber.includes(searchQuery)
  );

  const filteredMaterialIn = formData.itemDatas.filter((item) =>
    item.PermitTypeId === 10 &&
    item.Description.toLowerCase().includes(materialSearchQuery.toLowerCase())
  );

  const filteredMaterialOut = formData.itemDatas.filter((item) =>
    item.PermitTypeId === 20 &&
    item.Description.toLowerCase().includes(materialSearchQuery.toLowerCase())
  );

  const getSubmissionDateText = () => {
    const targetDate = headerStartOn || (startDate ? startDate.toISOString() : null);
    if (!targetDate) return "";
    try {
      const d = new Date(targetDate);
      if (isNaN(d.getTime())) return targetDate;
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return targetDate;
    }
  };

  const getSubmissionDateForPermit = (permitTypeId: number) => {
    const permitObj = loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === permitTypeId);
    const rawDate = permitObj?.CreatedOn || headerStartOn || (startDate ? startDate.toISOString() : null);
    if (!rawDate) return "-";
    try {
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return rawDate;
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return rawDate;
    }
  };

  const getPermitNumberForType = (permitTypeId: number) => {
    const permitObj = loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === permitTypeId);
    return permitObj?.PermitNumber || "";
  };

  const hasWorkPermit = selectedPermitTypes.includes(1);

  return (
    <>
      <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8 dark:bg-gray-900 font-sans transition-colors duration-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(backPath)}
                type="button"
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-semibold transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>

            </div>
          </div>

          <div className="bg-white rounded-3xl border-2 border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm dark:bg-gray-800 dark:border-gray-700">

            <div className="mb-6">
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white mb-6">
                Informasi Umum
              </h2>

              <SystemInformation
                nomorSuratIzin={formData.nomorSuratIzin}
                submittedBy={localStorage.getItem("user_email") || sessionStorage.getItem("user_email") || formData.PICAddress || ""}
                status={status}
                submissionDateTime={getSubmissionDateText()}
              />

              <div className="mb-6">
                <h3 className="text-xs font-bold text-slate-900 dark:text-white mb-3 uppercase tracking-wider">
                  Informasi Tenant / PIC
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Diajukan Oleh
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                      <select
                        value={formData.diajukanOleh}
                        disabled={isViewMode || permitHeaderStatus === 20 || hasAnyApprovedByDept}
                        onChange={(e) => setFormData({ ...formData, diajukanOleh: e.target.value })}
                        className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-750 dark:text-white dark:border-gray-600 appearance-none transition-all font-semibold disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      >
                        {PREPARED_BY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Nama Tenant
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        value={formData.TenantName}
                        disabled
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 text-slate-900 border border-slate-200 rounded-xl dark:bg-gray-700/50 dark:text-white dark:border-gray-600 cursor-not-allowed font-semibold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Lot
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        value={formData.lot}
                        disabled
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 text-slate-900 border border-slate-200 rounded-xl dark:bg-gray-700/50 dark:text-white dark:border-gray-600 cursor-not-allowed font-semibold"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-xs font-bold text-slate-900 dark:text-white mb-3 uppercase tracking-wider">
                  Penanggung Jawab (PIC)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Nama
                    </label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        value={formData.PICName}
                        disabled={isViewMode || permitHeaderStatus === 20 || hasAnyApprovedByDept}
                        onChange={(e) => setFormData({ ...formData, PICName: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-medium disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      No. Telepon
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        value={formData.PICPhone}
                        disabled={isViewMode || permitHeaderStatus === 20 || hasAnyApprovedByDept}
                        onChange={(e) => setFormData({ ...formData, PICPhone: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-medium disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="email"
                        value={formData.PICAddress}
                        disabled={isViewMode || permitHeaderStatus === 20 || hasAnyApprovedByDept}
                        onChange={(e) => setFormData({ ...formData, PICAddress: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-750 dark:text-white dark:border-gray-600 transition-all font-medium disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>




            <div className="mb-6">
              <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-4">
                Pilih Jenis Izin
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {permitTypes.map((type) => {
                  const isSelected = selectedPermitTypes.includes(type.value);
                  const isDisabled = isPermitTypeDisabled(type.value);

                  return (
                    <div
                      key={type.value}
                      onClick={() => !isDisabled && togglePermitType(type.value)}
                      className={`flex items-center gap-3 p-4 rounded-2xl border-2 select-none transition-all duration-200 ${isSelected
                        ? `border-blue-600 bg-blue-50/20 dark:border-blue-500 dark:bg-blue-950/20 shadow-xs ${isDisabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`
                        : isDisabled
                          ? "border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-800/50 opacity-40 cursor-not-allowed"
                          : "border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 cursor-pointer"
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                        ? "border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500"
                        : "border-slate-300 dark:border-gray-600"
                        }`}>
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className={`text-xs font-bold ${isSelected
                          ? "text-blue-900 dark:text-blue-200"
                          : isDisabled
                            ? "text-slate-400 dark:text-slate-500"
                            : "text-black dark:text-white"
                          }`}>
                          {type.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {hasWorkPermit && (
              <>
                <hr className="border-t-2 border-slate-300 dark:border-gray-600 my-8" />
                <div ref={sectionRefs[1]} style={{ scrollMarginTop: "80px" }} />
                <WorkPermitDetails
                  permitNumber={getPermitNumberForType(30)}
                  workCategory={workCategory}
                  setWorkCategory={handleWorkActivityChange}
                  workDescription={workDescription}
                  setWorkDescription={setWorkDescription}
                  allWorkers={formData.personnelDatas}
                  selectedWorkerIndices={workPermitWorkerIndices}
                  onAddWorker={(idx) => setWorkPermitWorkerIndices((prev) => [...prev, idx])}
                  onRemoveWorker={(idx) => setWorkPermitWorkerIndices((prev) => prev.filter((i) => i !== idx))}
                  onEditWorker={(idx) => openEditWorkerModal(formData.personnelDatas[idx], idx)}
                  onShowWorkerQR={(w) => setSelectedWorkerQR({ name: w.PersonnelName, qr: w.PersonnelQRCode || w.IdentityNumber })}
                  employeeOptions={employeeDropdownOptions}
                  onSelectEmployee={(emp) => handleSelectEmployee(emp, "wp")}
                  handleViewKtp={handleViewKtp}
                  options={workCategoryOptions}
                  startDate={wpStartDate}
                  endDate={wpEndDate}
                  setDateRange={setWpDateRange}
                  startTime={wpStartTime}
                  setStartTime={setWpStartTime}
                  endTime={wpEndTime}
                  setEndTime={setWpEndTime}
                  currentApprovalDepartment={getLastApprovalInfo(30).dept}
                  currentApprovalStatusName={getPermitStatusName(30)}
                  approvalDate={getLastApprovalInfo(30).date}
                  approvalSteps={loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 30)?.approvalDatas}
                  isReadOnly={isViewMode || permitHeaderStatus === 20 || loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 30)?.PermitStatus === 20}
                  hasAnyApprovedByDept={hasAnyApprovedByDept}
                  submissionDate={getSubmissionDateForPermit(30)}
                />
              </>
            )}

            {selectedPermitTypes.includes(2) && (
              <>
                <hr className="border-t-2 border-slate-300 dark:border-gray-600 my-8" />
                <div ref={sectionRefs[2]} style={{ scrollMarginTop: "80px" }} />
                <MaterialInPermitDetails
                  permitNumber={getPermitNumberForType(10)}
                  materialType={materialInReason}
                  setMaterialType={handleMaterialInActivityChange}
                  loadingArea={loadingArea}
                  setLoadingArea={setLoadingArea}
                  workDescription={workDescription}
                  setWorkDescription={setWorkDescription}
                  allWorkers={formData.personnelDatas}
                  selectedWorkerIndices={materialInWorkerIndices}
                  onAddWorker={(idx) => setMaterialInWorkerIndices((prev) => [...prev, idx])}
                  onRemoveWorker={(idx) => setMaterialInWorkerIndices((prev) => prev.filter((i) => i !== idx))}
                  onEditWorker={(idx) => openEditWorkerModal(formData.personnelDatas[idx], idx)}
                  onShowWorkerQR={(w) => setSelectedWorkerQR({ name: w.PersonnelName, qr: w.PersonnelQRCode || w.IdentityNumber })}
                  employeeOptions={employeeDropdownOptions}
                  onSelectEmployee={(emp) => handleSelectEmployee(emp, "matIn")}
                  handleViewKtp={handleViewKtp}
                  materialSearchQuery={materialSearchQuery}
                  setMaterialSearchQuery={setMaterialSearchQuery}
                  filteredMaterials={filteredMaterialIn}
                  handleDeleteMaterial={handleDeleteMaterial}
                  openEditMaterialModal={openEditMaterialModal}
                  openAddMaterialModal={openAddMaterialModal}
                  options={materialInOptions}
                  startDate={matInStartDate}
                  endDate={matInEndDate}
                  setDateRange={setMatInDateRange}
                  startTime={matInStartTime}
                  setStartTime={setMatInStartTime}
                  endTime={matInEndTime}
                  setEndTime={setMatInEndTime}
                  currentApprovalDepartment={getLastApprovalInfo(10).dept}
                  currentApprovalStatusName={getPermitStatusName(10)}
                  approvalDate={getLastApprovalInfo(10).date}
                  approvalSteps={loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 10)?.approvalDatas}
                  isReadOnly={isViewMode || permitHeaderStatus === 20 || loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 10)?.PermitStatus === 20}
                  hasAnyApprovedByDept={hasAnyApprovedByDept}
                  submissionDate={getSubmissionDateForPermit(10)}
                />
              </>
            )}

            {selectedPermitTypes.includes(3) && (
              <>
                <hr className="border-t-2 border-slate-300 dark:border-gray-600 my-8" />
                <div ref={sectionRefs[3]} style={{ scrollMarginTop: "80px" }} />
                <MaterialOutPermitDetails
                  permitNumber={getPermitNumberForType(20)}
                  materialType={materialOutReason}
                  setMaterialType={handleMaterialOutActivityChange}
                  loadingArea={loadingArea}
                  setLoadingArea={setLoadingArea}
                  workDescription={workDescription}
                  setWorkDescription={setWorkDescription}
                  allWorkers={formData.personnelDatas}
                  selectedWorkerIndices={materialOutWorkerIndices}
                  onAddWorker={(idx) => setMaterialOutWorkerIndices((prev) => [...prev, idx])}
                  onRemoveWorker={(idx) => setMaterialOutWorkerIndices((prev) => prev.filter((i) => i !== idx))}
                  onEditWorker={(idx) => openEditWorkerModal(formData.personnelDatas[idx], idx)}
                  onShowWorkerQR={(w) => setSelectedWorkerQR({ name: w.PersonnelName, qr: w.PersonnelQRCode || w.IdentityNumber })}
                  employeeOptions={employeeDropdownOptions}
                  onSelectEmployee={(emp) => handleSelectEmployee(emp, "matOut")}
                  handleViewKtp={handleViewKtp}
                  materialSearchQuery={materialSearchQuery}
                  setMaterialSearchQuery={setMaterialSearchQuery}
                  filteredMaterials={filteredMaterialOut}
                  handleDeleteMaterial={handleDeleteMaterial}
                  openEditMaterialModal={openEditMaterialModal}
                  openAddMaterialModal={openAddMaterialModal}
                  options={materialOutOptions}
                  startDate={matOutStartDate}
                  endDate={matOutEndDate}
                  setDateRange={setMatOutDateRange}
                  startTime={matOutStartTime}
                  setStartTime={setMatOutStartTime}
                  endTime={matOutEndTime}
                  setEndTime={setMatOutEndTime}
                  currentApprovalDepartment={getLastApprovalInfo(20).dept}
                  currentApprovalStatusName={getPermitStatusName(20)}
                  approvalDate={getLastApprovalInfo(20).date}
                  approvalSteps={loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 20)?.approvalDatas}
                  isReadOnly={isViewMode || permitHeaderStatus === 20 || loadedWorkPermitDatas.find((p: any) => p.PermitTypeId === 20)?.PermitStatus === 20}
                  hasAnyApprovedByDept={hasAnyApprovedByDept}
                  submissionDate={getSubmissionDateForPermit(20)}
                />
              </>
            )}

            <div className="flex justify-end gap-3 pt-6">
              {isViewMode || permitHeaderStatus === 20 || hasAnyApprovedByDept ? (
                <button
                  type="button"
                  onClick={() => navigate("/workPermit")}
                  className="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-xs font-bold dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700"
                >
                  Kembali
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleDiscardDraft}
                    className="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-xs font-bold dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all text-xs font-bold shadow-sm active:scale-98"
                  >
                    Kirim
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <WorkerModal
        isOpen={showWorkerModal}
        onClose={() => setShowWorkerModal(false)}
        mode={workerModalMode}
        worker={workerFormState}
        onSave={handleSaveWorker}
      />

      <MaterialModal
        isOpen={showMaterialModal}
        onClose={() => setShowMaterialModal(false)}
        mode={materialModalMode}
        material={materialFormState}
        onSave={handleSaveMaterial}
      />

      <ImagePreviewModal
        isOpen={showPreviewImageModal}
        onClose={() => setShowPreviewImageModal(false)}
        image={imagePreview}
      />

      {selectedWorkerQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedWorkerQR(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm shadow-xl z-10 border border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setSelectedWorkerQR(null)}
              className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-5 pt-6">
              <h2 className="text-base font-bold text-center text-gray-900 dark:text-white mb-1">
                QR Code Access
              </h2>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 text-center mb-5">
                {selectedWorkerQR.name}
              </p>

              <div className="flex justify-center mb-4">
                <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-inner">
                  <QRCodeSVG
                    id="worker-qr-code-svg"
                    value={selectedWorkerQR.qr}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>


              <div className="flex justify-center gap-2 mb-2 flex-wrap">
                <button
                  onClick={() => {
                    const svg = document.getElementById("worker-qr-code-svg");
                    if (!svg) return;
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = img.width;
                      canvas.height = img.height;
                      ctx?.drawImage(img, 0, 0);
                      const pngFile = canvas.toDataURL("image/png");
                      const downloadLink = document.createElement("a");
                      const cleanName = selectedWorkerQR.name.trim().replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
                      downloadLink.download = `QRCode-${cleanName}-${selectedWorkerQR.qr}.png`;
                      downloadLink.href = pngFile;
                      downloadLink.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-full text-xs font-medium hover:bg-gray-800 dark:hover:bg-gray-600 transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download QR
                </button>

                <button
                  onClick={() => {
                    const svg = document.getElementById("worker-qr-code-svg");
                    if (!svg) return;
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = async () => {
                      canvas.width = img.width;
                      canvas.height = img.height;
                      ctx?.drawImage(img, 0, 0);
                      const cleanName = selectedWorkerQR.name.trim().replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
                      const fileName = `QRCode-${cleanName}.png`;

                      if (navigator.canShare) {
                        canvas.toBlob(async (blob) => {
                          if (!blob) return;
                          const file = new File([blob], fileName, { type: "image/png" });
                          if (navigator.canShare({ files: [file] })) {
                            try {
                              await navigator.share({
                                title: `QR Code – ${selectedWorkerQR.name}`,
                                text: `QR Code Access untuk ${selectedWorkerQR.name}`,
                                files: [file],
                              });
                            } catch {
                              // user cancelled
                            }
                          } else {
                            window.open(`https://wa.me/?text=${encodeURIComponent(`QR Code Access untuk ${selectedWorkerQR.name}: ${selectedWorkerQR.qr}`)}`, "_blank");
                          }
                        }, "image/png");
                      } else {
                        window.open(`https://wa.me/?text=${encodeURIComponent(`QR Code Access untuk ${selectedWorkerQR.name}: ${selectedWorkerQR.qr}`)}`, "_blank");
                      }
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs font-medium transition"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Share ke WA
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}